﻿(() => {
  if (customElements.get('lamorial-product-bundle')) {
    return;
  }

  const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const tokenList = (value) => String(value || '').split('|').map(normalize).filter(Boolean);
  const variantText = (variant) => normalize([
    variant.title,
    variant.option1,
    variant.option2,
    variant.option3
  ].filter(Boolean).join(' '));
  const variantOptions = (variant) => [
    variant.option1,
    variant.option2,
    variant.option3,
    ...(Array.isArray(variant.options) ? variant.options : [])
  ].map(normalize).filter(Boolean);

  class LamorialProductBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.tierInputs = [...this.querySelectorAll('[data-lmb-tier-input]')];
      this.offerInputs = [...this.querySelectorAll('[data-lmb-offer-input]')];
      this.variantInput = this.querySelector('[data-lmb-variant-id]');
      this.quantityInput = this.querySelector('[data-lmb-quantity]');
      this.bonusProperty = this.querySelector('[data-lmb-bonus-property]');
      this.submitButton = this.querySelector('[data-lmb-submit]');
      this.submitLabel = this.querySelector('[data-lmb-submit-label]');
      this.guarantee = this.querySelector('[data-lmb-guarantee]');
      this.error = this.querySelector('[data-lmb-error]');
      this.form = this.querySelector('form');
      this.variants = this.parseVariants();

      this.tierInputs.forEach((input) => input.addEventListener('change', () => this.sync()));
      this.offerInputs.forEach((input) => input.addEventListener('change', () => this.sync()));
      this.submitButton?.addEventListener('click', () => this.sync());
      this.form?.addEventListener('submit', (event) => this.handleSubmit(event), true);
      this.sync();
    }

    parseVariants() {
      const variantsScript = this.querySelector('[data-lmb-variants]');
      if (!variantsScript) return [];
      try {
        return JSON.parse(variantsScript.textContent || '[]');
      } catch (_error) {
        return [];
      }
    }

    get tier() {
      return this.querySelector('[data-lmb-tier-input]:checked')?.value || 'standard';
    }

    get offer() {
      return this.querySelector('[data-lmb-offer-input]:checked')?.closest('[data-lmb-offer-wrap]');
    }

    sync() {
      const tier = this.tier;
      const offer = this.offer;
      if (!offer) return;

      const variant = this.variantFor(tier, offer);
      const cartQuantity = this.cartQuantityFor(offer, variant);

      this.dataset.tier = tier;
      this.updatePrices(tier);

      if (this.variantInput) {
        this.variantInput.value = variant?.id || '';
      }
      if (this.quantityInput) {
        this.quantityInput.value = cartQuantity;
      }
      if (this.bonusProperty) {
        this.bonusProperty.value = tier === 'full_stack' ? this.dataset.fullBonusText : this.dataset.standardBonusText;
      }
      if (this.submitLabel) {
        this.submitLabel.textContent = tier === 'full_stack' ? this.dataset.fullButtonLabel : this.dataset.standardButtonLabel;
      }
      if (this.guarantee) {
        this.guarantee.textContent = tier === 'full_stack' ? this.dataset.fullGuarantee : this.dataset.standardGuarantee;
      }

      const disabled = !this.variantInput?.value;
      if (this.submitButton) {
        this.submitButton.disabled = disabled;
      }
      this.dataset.error = disabled ? 'true' : 'false';
      if (this.error) {
        this.error.textContent = disabled ? 'Selected bundle is unavailable. Choose another offer.' : '';
      }
    }

    updatePrices(tier) {
      this.querySelectorAll('[data-lmb-price-current]').forEach((node) => {
        node.textContent = tier === 'full_stack' ? node.dataset.fullPrice : node.dataset.standardPrice;
      });
      this.querySelectorAll('[data-lmb-price-compare]').forEach((node) => {
        node.textContent = tier === 'full_stack' ? node.dataset.fullCompare : node.dataset.standardCompare;
      });
    }

    handleSubmit(event) {
      this.sync();

      if (this.variantInput?.value) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (this.submitButton) {
        this.submitButton.disabled = false;
      }
      if (this.error) {
        this.dataset.error = 'true';
        this.error.textContent = 'Selected bundle is unavailable. Choose another offer.';
      }
    }

    cartQuantityFor(offer, variant) {
      if (variant && variantOptions(variant).length > 1) {
        return 1;
      }
      if (this.dataset.variantQuantityMode === 'single' && this.manualVariantIdFor(this.tier, offer)) {
        return 1;
      }
      return offer.dataset.quantity || '1';
    }

    manualVariantIdFor(tier, offer) {
      return tier === 'full_stack' ? offer.dataset.fullVariantId : offer.dataset.standardVariantId;
    }

    variantFor(tier, offer) {
      const manualId = this.manualVariantIdFor(tier, offer);
      if (manualId) {
        return this.variants.find((variant) => String(variant.id) === String(manualId)) || { id: manualId, available: true };
      }

      if (this.dataset.autoVariantMatching !== 'true' || !this.variants.length) {
        return null;
      }

      const tierMatches = tokenList(tier === 'full_stack' ? this.dataset.fullMatchText : this.dataset.standardMatchText);
      const offerMatches = tokenList(offer.dataset.matchText);
      const tierLabel = normalize(tier === 'full_stack' ? this.dataset.fullTierLabel : this.dataset.standardTierLabel);
      const offerLabel = normalize(offer.dataset.offerLabel);
      const exactOptionMatch = this.variants.find((variant) => {
        const options = variantOptions(variant);
        return options.includes(tierLabel) && options.includes(offerLabel);
      });
      if (exactOptionMatch) {
        return exactOptionMatch;
      }

      const tierMatchedVariants = this.variants.filter((variant) => {
        const text = variantText(variant);
        return tierMatches.length === 0 || tierMatches.some((token) => text.includes(token));
      });

      const exact = tierMatchedVariants.find((variant) => {
        const text = variantText(variant);
        return offerMatches.length === 0 || offerMatches.some((token) => text.includes(token));
      });

      return exact || null;
    }
  }

  customElements.define('lamorial-product-bundle', LamorialProductBundle);
})();



